/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React from "react";
import { Sun, Moon, Check } from "lucide-react";
import { AppTheme } from "../../types";

interface ThemeSelectorProps {
  currentTheme: AppTheme;
  onThemeChange: (theme: AppTheme) => void;
  variant?: "compact" | "full" | "dropdown";
  className?: string;
}

export const THEME_OPTIONS: Array<{
  id: AppTheme;
  label: string;
  shortLabel: string;
  icon: React.ReactNode;
  description: string;
  badgeColor: string;
}> = [
  {
    id: "light",
    label: "Claro (Padrão)",
    shortLabel: "Claro",
    icon: <Sun size={15} className="text-amber-500" />,
    description: "Tema claro, limpo e com alto contraste para leitura perfeita.",
    badgeColor: "bg-amber-100 text-amber-800 border-amber-300",
  },
  {
    id: "dark",
    label: "Escuro",
    shortLabel: "Escuro",
    icon: <Moon size={15} className="text-blue-400" />,
    description: "Tema escuro profissional em tons neutros de slate/zinc.",
    badgeColor: "bg-zinc-800 text-zinc-200 border-zinc-700",
  },
];

export const ThemeSelector: React.FC<ThemeSelectorProps> = ({
  currentTheme,
  onThemeChange,
  variant = "compact",
  className = "",
}) => {
  if (variant === "full") {
    return (
      <div className={`space-y-2.5 ${className}`}>
        <div className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-zinc-400 opacity-80 mb-2">
          Tema da Interface
        </div>
        <div className="grid grid-cols-1 gap-2">
          {THEME_OPTIONS.map((opt) => {
            const isSelected = currentTheme === opt.id;
            return (
              <button
                key={opt.id}
                type="button"
                onClick={() => onThemeChange(opt.id)}
                className={`w-full p-3 rounded-2xl border text-left transition-all flex items-center justify-between cursor-pointer ${
                  isSelected
                    ? "bg-amber-400/10 border-amber-400/80 shadow-md ring-1 ring-amber-400/50"
                    : "bg-black/5 dark:bg-zinc-900/50 border-black/10 dark:border-zinc-800 hover:border-black/20 dark:hover:border-zinc-700"
                }`}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`p-2 rounded-xl border flex items-center justify-center ${
                      isSelected
                        ? "bg-amber-400 text-emerald-950 border-amber-400"
                        : "bg-black/10 dark:bg-zinc-800 border-transparent text-slate-700 dark:text-zinc-300"
                    }`}
                  >
                    {opt.icon}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-black tracking-wide">
                        {opt.label}
                      </span>
                      {opt.id === "light" && (
                        <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-700 dark:text-emerald-300">
                          Padrão
                        </span>
                      )}
                    </div>
                    <p className="text-[11px] text-slate-500 dark:text-zinc-400 mt-0.5">
                      {opt.description}
                    </p>
                  </div>
                </div>

                {isSelected && (
                  <div className="w-5 h-5 rounded-full bg-amber-400 text-emerald-950 flex items-center justify-center font-black">
                    <Check size={12} strokeWidth={3} />
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // Variant "compact" (for header / navigation bars)
  return (
    <div
      className={`flex items-center p-1 rounded-xl border bg-black/5 dark:bg-zinc-900/80 border-black/10 dark:border-zinc-800 gap-1 ${className}`}
      role="group"
      aria-label="Seleção de Tema Visual"
    >
      {THEME_OPTIONS.map((opt) => {
        const isSelected = currentTheme === opt.id;
        return (
          <button
            key={opt.id}
            type="button"
            onClick={() => onThemeChange(opt.id)}
            className={`px-2 py-1 rounded-lg text-xs font-bold transition flex items-center gap-1.5 cursor-pointer select-none ${
              isSelected
                ? "bg-amber-400 text-emerald-950 font-black shadow-sm"
                : "text-slate-600 dark:text-zinc-400 hover:text-slate-900 dark:hover:text-white hover:bg-black/5 dark:hover:bg-zinc-800/60"
            }`}
            title={`Alternar para tema ${opt.label}`}
            aria-pressed={isSelected}
          >
            <span className="shrink-0">{opt.icon}</span>
            <span className="hidden md:inline text-[11px] font-extrabold">{opt.shortLabel}</span>
          </button>
        );
      })}
    </div>
  );
};
